// Which children a contracting agency may see — the one statement of it, for
// the staffing board, the care plans served through the portal, the weekly
// Chat update (edge functions, through _shared/lib) and the office's own view
// of who can see a child (the care plan card).
//
// Three ways a child belongs to an agency, and no others — no county or skill
// inference. An agency seeing a child is a decision someone here made:
//
//   1. a recorded assignment, proposed or active;
//   2. a live offer to that agency — waiting for their answer and not yet
//      expired, or accepted (lib/agencyOffers.js). Declined, expired,
//      withdrawn and filled offers show nothing;
//   3. the agency's own nurses on the child's calendar in the window asked
//      about, because a child staffed in practice should not vanish from the
//      board for want of a written assignment.
//
// Runtime-neutral: it takes the Supabase client it is given, so the browser
// passes the signed-in user's and a function passes the service role's.

import { LIVE_OFFER_STATUSES, isLiveOffer } from './agencyOffers.js';

const DAY = 86_400_000;

/** The window the portal's care plan and the office card both use: two weeks back, five ahead. */
export const careWindow = (now = Date.now()) => ({ from: new Date(now - 14 * DAY), to: new Date(now + 35 * DAY) });

const must = (res, what) => {
  if (res.error) throw new Error(`Could not read ${what}: ${res.error.message}`);
  return res.data || [];
};

/**
 * The children one agency may see in [window.from, window.to), with the rows
 * that put them there.
 * @returns {Promise<{ ids: Set<string>, agencyStaff: object[], assignRows: object[], offerRows: object[] }>}
 */
export async function agencyClientSlice(sb, agencyId, window) {
  const agencyStaff = must(await sb.from('staff').select('id, first_name, last_name, position_code')
    .eq('contracting_agency_id', agencyId), "the agency's nurses");
  const staffIds = agencyStaff.map((s) => s.id);
  const [assigned, offered, visits] = await Promise.all([
    sb.from('client_staff_assignments').select('client_id, staff_id')
      .eq('contracting_agency_id', agencyId).in('status', ['proposed', 'active']),
    sb.from('client_agency_offers')
      .select('id, client_id, status, service_type, role_needed, hours_needed, start_date, note, expires_at, responded_at, responded_by_name, created_at')
      .eq('contracting_agency_id', agencyId).in('status', LIVE_OFFER_STATUSES),
    staffIds.length
      ? sb.from('service_visits').select('client_id').in('staff_id', staffIds)
        .gte('scheduled_start', window.from.toISOString()).lt('scheduled_start', window.to.toISOString())
        .not('client_id', 'is', null)
      : Promise.resolve({ data: [], error: null }),
  ]);
  const assignRows = must(assigned, 'assignments');
  const offerRows = must(offered, 'offers').filter((o) => isLiveOffer(o));
  const ids = new Set([
    ...assignRows.map((r) => r.client_id),
    ...offerRows.map((r) => r.client_id),
    ...must(visits, 'visits').map((r) => r.client_id),
  ].filter(Boolean));
  return { ids, agencyStaff, assignRows, offerRows };
}

/**
 * The same rule turned round: for one child, which agencies see them and why.
 * @returns {Promise<Map<string, { offerId: string|null, offer: object|null, why: string[] }>>} keyed by agency id
 */
export async function agenciesSeeingClient(sb, clientId, window = careWindow()) {
  const [offered, assigned, visits] = await Promise.all([
    sb.from('client_agency_offers').select('id, contracting_agency_id, status, expires_at, role_needed, hours_needed, start_date, responded_by_name, response_note')
      .eq('client_id', clientId).in('status', LIVE_OFFER_STATUSES),
    sb.from('client_staff_assignments').select('contracting_agency_id').eq('client_id', clientId).in('status', ['proposed', 'active']),
    sb.from('service_visits').select('staff:staff_id ( contracting_agency_id )').eq('client_id', clientId)
      .gte('scheduled_start', window.from.toISOString()).lt('scheduled_start', window.to.toISOString()),
  ]);
  const out = new Map();
  const add = (agencyId, why, offerId = null) => {
    if (!agencyId) return;
    const e = out.get(agencyId) || { offerId: null, offer: null, why: [] };
    if (!e.why.includes(why)) e.why.push(why);
    if (offerId) e.offerId = offerId;
    out.set(agencyId, e);
  };
  must(offered, 'offers').filter((o) => isLiveOffer(o)).forEach((o) => {
    add(o.contracting_agency_id, o.status === 'accepted' ? 'accepted the offer' : 'offered', o.id);
    out.get(o.contracting_agency_id).offer = o;
  });
  must(assigned, 'assignments').forEach((a) => add(a.contracting_agency_id, 'assigned'));
  must(visits, 'visits').forEach((v) => add(v.staff?.contracting_agency_id, 'their nurses are scheduled'));
  return out;
}

/** Whether an agency may still use the portal at all. Pending agencies credential before they start. */
export const agencyMayUsePortal = (status) => status !== 'terminated' && status !== 'expired';
